import { Auth } from '@aws-amplify/auth';
import { CognitoRefreshToken, CognitoUser, CognitoUserAttribute, CognitoUserSession } from 'amazon-cognito-identity-js';
import { CognitoRetryOptions, retryCognitoInMeCentral1 } from './CognitoRetry';

export const cognitoAuth = {
  signIn(username: string, password: string, options?: CognitoRetryOptions) {
    return retryCognitoInMeCentral1('Auth.signIn', () => Auth.signIn(username, password), options);
  },

  signUp(input: Parameters<typeof Auth.signUp>[0], options?: CognitoRetryOptions) {
    return retryCognitoInMeCentral1('Auth.signUp', () => Auth.signUp(input), options);
  },

  resendSignUp(username: string, options?: CognitoRetryOptions) {
    return retryCognitoInMeCentral1('Auth.resendSignUp', () => Auth.resendSignUp(username), options);
  },

  confirmSignUp(username: string, code: string, options?: CognitoRetryOptions) {
    return retryCognitoInMeCentral1('Auth.confirmSignUp', () => Auth.confirmSignUp(username, code), options);
  },

  currentSession(options?: CognitoRetryOptions) {
    return retryCognitoInMeCentral1('Auth.currentSession', () => Auth.currentSession(), options);
  },

  currentAuthenticatedUser(options?: CognitoRetryOptions) {
    return retryCognitoInMeCentral1('Auth.currentAuthenticatedUser', () => Auth.currentAuthenticatedUser(), options);
  },

  completeNewPassword(user: any, newPassword: string, requiredAttributes: any, options?: CognitoRetryOptions) {
    return retryCognitoInMeCentral1(
      'Auth.completeNewPassword',
      () => Auth.completeNewPassword(user, newPassword, requiredAttributes),
      options,
    );
  },

  changePassword(currentUser: any, oldPassword: string, newPassword: string, options?: CognitoRetryOptions) {
    return retryCognitoInMeCentral1(
      'Auth.changePassword',
      () => Auth.changePassword(currentUser, oldPassword, newPassword),
      options,
    );
  },

  forgotPassword(username: string, options?: CognitoRetryOptions) {
    return retryCognitoInMeCentral1('Auth.forgotPassword', () => Auth.forgotPassword(username), options);
  },

  forgotPasswordSubmit(username: string, verificationCode: string, newPassword: string, options?: CognitoRetryOptions) {
    return retryCognitoInMeCentral1(
      'Auth.forgotPasswordSubmit',
      () => Auth.forgotPasswordSubmit(username, verificationCode, newPassword),
      options,
    );
  },

  signOut(input?: Parameters<typeof Auth.signOut>[0], options?: CognitoRetryOptions) {
    return retryCognitoInMeCentral1('Auth.signOut', () => Auth.signOut(input), options);
  },

  getUserAttributes(cognitoUser: CognitoUser, options?: CognitoRetryOptions): Promise<CognitoUserAttribute[]> {
    return retryCognitoInMeCentral1(
      'CognitoUser.getUserAttributes',
      () =>
        new Promise((resolve, reject) => {
          cognitoUser.getUserAttributes((error, attributes) => {
            if (error) {
              reject(error);
              return;
            }

            resolve(attributes ?? []);
          });
        }),
      options,
    );
  },

  refreshSession(
    cognitoUser: CognitoUser,
    refreshToken: CognitoRefreshToken,
    options?: CognitoRetryOptions,
  ): Promise<CognitoUserSession> {
    return retryCognitoInMeCentral1(
      'CognitoUser.refreshSession',
      () =>
        new Promise((resolve, reject) => {
          cognitoUser.refreshSession(refreshToken, (error, session) => {
            if (error) {
              reject(error);
              return;
            }

            if (!session) {
              reject(new Error('Cognito refreshSession completed without a session'));
              return;
            }

            resolve(session);
          });
        }),
      options,
    );
  },
};
